/**
 * CreateGRN — prefilled from a PO, editable qty per line, inspection notes.
 * Saves as DRAFT; user later posts from GRNDetail.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Save, ChevronLeft, Building2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import BackButton from '../../../../components/admin/BackButton';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateGRN = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [step, setStep] = useState(1);
  const [pos, setPos] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [template, setTemplate] = useState(null);
  const [form, setForm] = useState({
    received_date: new Date().toISOString().slice(0, 10),
    warehouse_id: '',
    notes: '',
    tolerance_pct: 5,
    items: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, w] = await Promise.all([
          axios.get(`${API_URL}/api/procurement/purchase-orders`, auth),
          axios.get(`${API_URL}/api/inventory/warehouses`, auth).catch(() => ({ data: [] })),
        ]);
        // Only POs that are confirmed or partially delivered can receive goods.
        const eligibleStatuses = ['sent_to_supplier', 'supplier_confirmed', 'partially_delivered', 'approved'];
        setPos((p.data || []).filter((x) => eligibleStatuses.includes(x.status)));
        setWarehouses(w.data || []);
      } catch (_) { /* ignore */ }
    })();
  }, []); // eslint-disable-line

  const loadTemplate = async (po_id) => {
    if (!po_id) return;
    try {
      const r = await axios.get(`${API_URL}/api/procurement/grns/from-po/${po_id}`, auth);
      setTemplate(r.data);
      setForm((p) => ({
        ...p,
        received_date: r.data.received_date?.slice(0, 10) || p.received_date,
        items: r.data.items,
      }));
      setStep(2);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.response?.data?.detail || 'Failed to load PO template.' });
    }
  };

  const updateItem = (idx, field, value) => {
    setForm((p) => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [field]: field === 'qty_received' || field === 'rejected_quantity' ? parseFloat(value || 0) : value };
      return { ...p, items };
    });
  };

  const getDiscrepancy = (it) => {
    const pending = Math.max((it.qty_ordered || 0) - (it.qty_previously_received || 0), 0);
    const over = (it.qty_received || 0) - pending;
    const maxAllowed = pending * (1 + (form.tolerance_pct || 0) / 100);
    return {
      pending,
      over,
      exceeds: (it.qty_received || 0) > maxAllowed + 1e-6,
      underDelivery: (it.qty_received || 0) < pending && (it.qty_received || 0) > 0,
    };
  };

  const hasViolation = form.items.some((it) => getDiscrepancy(it).exceeds);

  const save = async () => {
    if (hasViolation) {
      toast({ variant: 'destructive', title: 'Over-delivery', description: 'At least one line exceeds the tolerance. Adjust before saving.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        po_id: selectedPoId,
        received_date: new Date(form.received_date).toISOString(),
        warehouse_id: form.warehouse_id || null,
        notes: form.notes,
        tolerance_pct: Number(form.tolerance_pct),
        items: form.items,
      };
      const r = await axios.post(`${API_URL}/api/procurement/grns`, payload, auth);
      toast({ title: 'GRN created', description: `${r.data.grn_number} saved as draft.` });
      navigate(`/admin/procurement/grns/${r.data.id}`);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="create-grn-page">
      <BackButton />
      <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
        <Package className="w-6 h-6 text-[#023E8A]" /> New Goods Received Note
      </h1>

      {/* Step 1 — pick PO */}
      {step === 1 && (
        <Card>
          <CardHeader><CardTitle>Step 1 — Select a Purchase Order</CardTitle></CardHeader>
          <CardContent>
            {pos.length === 0 ? (
              <div className="text-center py-10 text-[#6C757D]">
                <Info className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                No Purchase Orders eligible for receipt (must be confirmed or partially delivered).
              </div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {pos.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setSelectedPoId(p.id); loadTemplate(p.id); }}
                    className="w-full text-left border rounded-lg p-3 hover:bg-[#f0fbfd] hover:border-[#00B4D8] transition"
                    data-testid={`create-grn-po-${p.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <code className="text-xs text-[#023E8A] bg-[#023E8A]/10 px-2 py-0.5 rounded">{p.po_number}</code>
                        <div className="text-sm font-semibold mt-1 text-[#2B2D42]">
                          {p.supplier_snapshot?.company_name}
                        </div>
                        <div className="text-xs text-[#6C757D]">
                          {p.items?.length || 0} line(s) · {p.currency} {Number(p.total_amount || 0).toLocaleString()}
                          {p.delivery_percentage > 0 && ` · ${p.delivery_percentage}% delivered`}
                        </div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 capitalize">
                        {p.status.replace('_', ' ')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2 — receive */}
      {step === 2 && template && (
        <>
          <Card className="border-[#00B4D8]">
            <CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <button onClick={() => setStep(1)} className="text-sm text-[#023E8A] hover:underline inline-flex items-center gap-1" data-testid="create-grn-back-step1">
                <ChevronLeft className="w-4 h-4" /> Change PO
              </button>
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs text-[#6C757D]">Purchase Order</div>
                <div className="font-bold text-[#023E8A]">{template.po_number}</div>
              </div>
              <div>
                <div className="text-xs text-[#6C757D]">Supplier</div>
                <div className="font-semibold text-sm inline-flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" />{template.supplier_snapshot?.company_name}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#6C757D]">Issuer</div>
                <div className="font-semibold text-sm">{template.company_snapshot?.trading_name || template.company_snapshot?.legal_name}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Delivery details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-[#6C757D]">Received date *</label>
                  <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} data-testid="grn-received-date" />
                </div>
                <div>
                  <label className="text-xs text-[#6C757D]">Warehouse</label>
                  <select className="w-full px-3 py-2 border rounded text-sm bg-white" value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} data-testid="grn-warehouse">
                    <option value="">— Select —</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name || w.code}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6C757D]">Tolerance (%)</label>
                  <Input type="number" min="0" max="100" step="0.1" value={form.tolerance_pct} onChange={(e) => setForm({ ...form, tolerance_pct: e.target.value })} data-testid="grn-tolerance" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#6C757D]">Notes</label>
                <textarea rows={2} className="w-full px-3 py-2 border rounded text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="grn-notes" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Line items — Received quantities</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="grn-items-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-3 py-2">Product</th>
                      <th className="text-right px-3 py-2">Ordered</th>
                      <th className="text-right px-3 py-2">Prev. received</th>
                      <th className="text-right px-3 py-2">Pending</th>
                      <th className="text-right px-3 py-2">Receive now *</th>
                      <th className="text-right px-3 py-2">Rejected</th>
                      <th className="text-left px-3 py-2">Inspection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => {
                      const d = getDiscrepancy(it);
                      return (
                        <tr key={idx} className={`border-b ${d.exceeds ? 'bg-red-50' : ''}`} data-testid={`grn-item-${idx}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-[#2B2D42]">{it.product_name}</div>
                            {it.description && <div className="text-xs text-[#6C757D]">{it.description}</div>}
                          </td>
                          <td className="text-right px-3 py-2">{it.qty_ordered}</td>
                          <td className="text-right px-3 py-2">{it.qty_previously_received}</td>
                          <td className="text-right px-3 py-2 font-semibold">{d.pending}</td>
                          <td className="text-right px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.qty_received}
                              onChange={(e) => updateItem(idx, 'qty_received', e.target.value)}
                              className={`w-24 text-right px-2 py-1 border rounded ${d.exceeds ? 'border-red-500 bg-red-50' : ''}`}
                              data-testid={`grn-item-qty-${idx}`}
                            />
                            {d.exceeds && (
                              <div className="text-[10px] text-red-700 mt-0.5 inline-flex items-center gap-0.5">
                                <AlertTriangle className="w-3 h-3" /> Exceeds tolerance
                              </div>
                            )}
                          </td>
                          <td className="text-right px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.rejected_quantity || 0}
                              onChange={(e) => updateItem(idx, 'rejected_quantity', e.target.value)}
                              className="w-20 text-right px-2 py-1 border rounded"
                              data-testid={`grn-item-rejected-${idx}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              value={it.inspection_notes || ''}
                              onChange={(e) => updateItem(idx, 'inspection_notes', e.target.value)}
                              placeholder="Damage / observations…"
                              className="w-full px-2 py-1 border rounded text-xs"
                              data-testid={`grn-item-insp-${idx}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/procurement/grns')} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving || hasViolation} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="grn-save-draft">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save as Draft'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateGRN;
